import {useEffect, useRef, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router'
import {getToken} from '@/utils/authToken'
import {userService} from '@/services/userService'
import {checkRefOwnership} from '@/services/phoneBookingAuthService'
import {saveRedirectAfterLogin} from '@/utils/functions'
import {Loader} from '@/customComponents/uiComponents/Loader.jsx'

/**
 * Handoff links now carry `?ref=` — an opaque handle on ONE trip — where they used to
 * carry `?token=AL-…`, an auto-login credential this gate consumed on the spot. That is
 * the whole difference: a ref is not a session, so nobody is logged in by opening a link
 * any more. This gate decides who needs to prove themselves, and sends them to
 * `/authorize` when they do.
 *
 * A link arrives on any route, e.g.
 *   /rides/1251/confirm?ref=AL-…&layout=minimal
 *
 * and resolves to exactly one of three outcomes:
 *
 *  1. No session (or a stale one) → remember the clean URL and go to `/authorize?ref=…`,
 *     which sends the OTP and comes back here after it verifies.
 *  2. Session, and the trip is theirs → strip `ref` and stay put. No OTP: they are
 *     already the passenger this link was meant for.
 *  3. Session, but the trip is someone else's → `/authorize?ref=…` as well. The link was
 *     forwarded, or this device is signed into a different account.
 *
 * `ref` is carried THROUGH to /authorize (it is the key that page works from) and dropped
 * on the way back out, so the passenger never sees it in the address bar of the page they
 * actually wanted.
 *
 * While it resolves it renders a loader INSTEAD of <Routes>, so ProtectedRoute can't run
 * mid-decision and flash /signin.
 */

const AUTH_ROUTES = ['/signin', '/signup', '/otp-verification', '/authorize']
const isAuthRoute = (path) => AUTH_ROUTES.some((p) => path.startsWith(p))

function readRef(search) {
    const value = new URLSearchParams(search).get('ref')
    return value || null
}

// The current URL with `ref` removed — the page the link actually points at. Keeps the
// path, every other param (`layout=minimal` matters) and the hash.
//
// The ref comes off on EVERY route, including /phone-booking-confirmation: once the
// passenger is through, it has done its job and the page they wanted should not carry a
// handoff key in its address bar. The cost is that a session which lapses while that page
// is open has nothing left to re-authorise with — PhoneBookingRouteGate then asks the
// passenger to reopen the link from their message, which is where a fresh ref lives.
function destWithoutRef() {
    const params = new URLSearchParams(window.location.search)
    params.delete('ref')
    const qs = params.toString()
    return `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
}

export default function RefAuthGate({children}) {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()

    const ref = readRef(searchParams.toString())
    // Resolve synchronously on first paint so protected routes never render (and
    // redirect) before the decision is made.
    const [resolving, setResolving] = useState(() => {
        const initial = readRef(window.location.search)
        return !!initial && !isAuthRoute(window.location.pathname)
    })
    // Which ref we've already started on — guards re-entry and StrictMode's double-run.
    const startedRef = useRef(null)

    useEffect(() => {
        // /authorize reads the ref itself; leaving it alone there is what lets that page
        // keep working after we send someone to it.
        if (!ref || isAuthRoute(window.location.pathname)) {
            setResolving(false)
            return
        }
        if (startedRef.current === ref) return
        startedRef.current = ref
        setResolving(true)

        // Captured NOW, before any async work or a stray ProtectedRoute bounce can change
        // the URL out from under us.
        const dest = destWithoutRef()

        const toAuthorize = () => {
            saveRedirectAfterLogin(dest)
            navigate(`/authorize?ref=${encodeURIComponent(ref)}`, {replace: true})
        }

        ;(async () => {
            try {
                if (!getToken()) {
                    toAuthorize()
                    return
                }

                // A token in storage isn't a live session — it expires, and it survives a
                // password reset elsewhere. 200 = still good; 401 = stale (apiClient has
                // already cleared it).
                const acct = await userService.getAccount()
                if (!acct.success) {
                    toAuthorize()
                    return
                }

                // Whose trip is it? Answered from `GET /phone-booking/{ref}` — the same
                // summary /authorize runs on — against the account we just fetched.
                // `owned: null` = the check couldn't be made, and we carry on with the
                // session we have: the trip endpoints refuse someone else's trip anyway,
                // so an unanswerable check costs a passenger an empty page at worst, where
                // guessing "not owned" would send every legitimate passenger through an
                // OTP they don't need.
                const {owned} = await checkRefOwnership(ref, acct.data)
                if (owned === false) {
                    toAuthorize()
                    return
                }

                navigate(dest, {replace: true})
            } finally {
                setResolving(false)
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref])

    if (resolving) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader/>
            </div>
        )
    }

    return children
}
